import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Star, Leaf, Sparkle } from 'lucide-react';
import { IMG } from '../mock';

export default function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-sage">
      <div className="leaf-blob -top-10 -left-20" />
      <div className="leaf-blob bottom-10 right-0" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-6 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E6EBD5] text-[#1B4631] text-xs font-semibold border border-[#CFDAB1]">
            <Leaf className="w-3.5 h-3.5" />
            Mediterranean • Anti-inflammatory • Chef-crafted
          </div>
            <h1 className="font-display text-[44px] sm:text-6xl lg:text-[78px] leading-[0.98] font-semibold mt-5 text-[#1B2A22]">
            Fresh Catering for <span className="italic text-[#245A40]">Chicago&rsquo;s Busiest Teams</span>
          </h1>
          <p className="mt-6 text-xl sm:text-2xl text-[#1B2A22] max-w-xl leading-relaxed font-medium">
            Healthy, delicious meals delivered to your office. Order in minutes.
          </p>
          <p className="mt-3 text-base text-[#3F4A42] max-w-xl leading-relaxed">
            The best Mediterranean bowls, custom grain bowls and healthy salad wraps in Chicago — made from scratch each morning using only olive oil, ghee, butter and beef tallow. Never seed oils.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button asChild className="btn-primary rounded-full h-14 px-10 font-bold text-lg shadow-lg shadow-[#245A40]/25" data-testid="hero-order-now-btn">
              <Link to="/catering-inquiry">Order Now <ArrowRight className="w-5 h-5 ml-1.5" /></Link>
            </Button>
            <Button asChild variant="ghost" className="btn-ghost rounded-full h-14 px-7 font-semibold text-base border border-[#1B2A22]/15">
              <a href="#menu">Browse the menu</a>
            </Button>
          </div>

          <div className="mt-10 flex items-center gap-6">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#E8A33B] text-[#E8A33B]" />
              ))}
            </div>
            <div className="text-sm text-[#3F4A42]">
              <span className="font-semibold text-[#1B2A22]">5.0</span> from 31 Google reviews
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div className="grid grid-cols-6 grid-rows-6 gap-3 sm:gap-4 h-[460px] sm:h-[580px]">
            <div className="col-span-4 row-span-4 rounded-[28px] overflow-hidden bg-[#E6EBD5] grain">
              <img src={IMG.roastedVeg} alt="Custom grain bowls catering — roasted vegetable plant-based bowl, Wholesome 360 Chicago" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 row-span-3 rounded-[24px] overflow-hidden bg-[#E6EBD5]">
              <img src={IMG.figFeta} alt="Healthy salad wraps Chicago — Greek fig and feta salad catering" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 row-span-3 rounded-[24px] overflow-hidden bg-[#E6EBD5]">
              <img src={IMG.powerBowl} alt="Best Mediterranean bowls Chicago — power bowl with skirt steak, office catering" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-4 row-span-2 rounded-[24px] overflow-hidden bg-[#E6EBD5]">
              <img src={IMG.spread} alt="Plant-based catering Chicago — corporate spread of Mediterranean bowls and salads" className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="absolute -bottom-6 -left-3 sm:left-6 bg-[#F6F2E8] rounded-2xl shadow-[0_22px_44px_-22px_rgba(0,0,0,0.22)] p-4 w-60 border border-[#D6CFB8] rotate-[-4deg]">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#245A40] grid place-items-center">
                <Sparkle className="w-4 h-4 text-[#E8C56B]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#6F6A55]">Made fresh daily</div>
                <div className="text-sm font-semibold text-[#1B2A22]">From-scratch sauces</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-y border-[#D6CFB8] bg-[#ECE7D6]/70 overflow-hidden relative">
        <div className="flex whitespace-nowrap marquee py-4">
          {[...Array(2)].flatMap((_, j) => [
            'Olive oil', 'Ghee', 'Butter', 'Beef tallow', 'No seed oils', 'Never Fried', 'Organic greens',
            'From-scratch sauces', 'Mediterranean-inspired', 'Anti-inflammatory', '10 years in Chicago',
          ].map((t, i) => (
            <span key={`${j}-${i}`} className="px-7 text-sm uppercase tracking-[0.24em] font-medium text-[#1B2A22]/75">
              {t} <Leaf className="inline w-3 h-3 text-[#245A40] mx-2 -mt-0.5" />
            </span>
          )))}
        </div>
      </div>
    </section>
  );
}
